import {
  BarChart3,
  Bell,
  BellRing,
  Blocks,
  FileSearch,
  LifeBuoy,
  LogOut,
  Menu,
  ShieldCheck,
  Users,
  Wrench,
  X,
} from "@tamagui/lucide-icons";
import { useAuth, useUserRole, UserRole } from "common";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  Button,
  YStack,
  XStack,
  Image,
  Separator,
  useMedia,
  Spinner,
  ScrollView,
  Text,
  View,
} from "tamagui";

import { Navigation } from "./Navigation";
import logoImage from "../assets/images/logo.svg";
import { iconLabel } from "../utils/a11y";

export const Sidebar = ({
  isOpen,
  onOpenChange,
}: {
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}) => {
  const { lg, md } = useMedia();
  const { t } = useTranslation();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const close = () => onOpenChange?.(false);

  useEffect(() => {
    if (!md || !isOpen) return undefined;
    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange?.(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [md, isOpen]);

  if (!md) {
    return (
      <ScrollView
        minWidth={lg ? 240 : 300}
        maxWidth={lg ? 240 : 300}
        minHeight="100%"
        backgroundColor="$gray2"
        padding="$3.5"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <SidebarContent />
      </ScrollView>
    );
  }

  if (!isOpen) return null;

  // Narrow windows: the nav is a drawer over the page, opened from the top bar.
  // Absolute is enough: Layout's root is the full viewport and never scrolls.
  // Layout makes the page behind inert and restores focus on close.
  return (
    <View
      position="absolute"
      top={0}
      right={0}
      bottom={0}
      left={0}
      zIndex={1000}
      role="dialog"
      aria-modal
      aria-label={t("sidebar.openMenu")}
    >
      <View
        position="absolute"
        top={0}
        right={0}
        bottom={0}
        left={0}
        backgroundColor="black"
        opacity={0.6}
        onPress={close}
      />
      <ScrollView
        position="absolute"
        top={0}
        bottom={0}
        left={0}
        width={300}
        maxWidth="85%"
        backgroundColor="$gray2"
        padding="$3.5"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <SidebarContent />
        <Button
          ref={closeButtonRef}
          position="absolute"
          top={0}
          right={0}
          size="$4"
          circular
          chromeless
          icon={X}
          onPress={close}
          {...iconLabel(t("sidebar.closeMenu"))}
        />
      </ScrollView>
    </View>
  );
};

export const MobileTopBar = ({ onMenuPress }: { onMenuPress: () => void }) => {
  const { t } = useTranslation();

  return (
    <XStack
      height={56}
      flexShrink={0}
      alignItems="center"
      gap="$3"
      paddingHorizontal="$3"
      backgroundColor="$gray2"
      borderBottomWidth={1}
      borderColor="$borderColor"
    >
      <Button
        size="$4"
        circular
        chromeless
        icon={Menu}
        onPress={onMenuPress}
        {...iconLabel(t("sidebar.openMenu"))}
      />
      <Image
        source={{ uri: logoImage, width: 32, height: 32 }}
        width={32}
        height={32}
        resizeMode="contain"
        alt={t("sidebar.logoAlt")}
      />
    </XStack>
  );
};

const SidebarContent = () => {
  const { role } = useUserRole();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isLoggingOut, setIsLoggingOut] = useState(false);
  // Web-admin is operator-only. Homeowners (UserRole.USER) never reach this
  // sidebar — ProtectedRoute renders AccessDenied for them.
  const isOperator = role === UserRole.INSTALLER || role === UserRole.ADMIN;

  const onLogOutPress = async () => {
    setIsLoggingOut(true);
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error(`logout failure, error: ${JSON.stringify(error)}`);
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <YStack gap="$4" flex={1}>
      <Image
        source={{ uri: logoImage, width: 70, height: 70 }}
        width="100%"
        height={70}
        resizeMode="stretch"
        alt={t("sidebar.logoAlt")}
        alignSelf="center"
      />

      <Separator />

      {!isOperator && <Spinner size="large" />}

      <Navigation>
        {isOperator ? (
          <>
            <Navigation.Button icon={Bell} routePath="/alerts">
              {t("sidebar.alerts")}
            </Navigation.Button>
            <Navigation.Button icon={Blocks} routePath="/base-stations">
              {t("sidebar.baseStations")}
            </Navigation.Button>
            <Navigation.Button icon={Wrench} routePath="/service-requests">
              {t("sidebar.serviceRequests")}
            </Navigation.Button>
            <Navigation.Button icon={Users} routePath="/customers">
              {t("sidebar.customers")}
            </Navigation.Button>
            <Navigation.Button icon={BarChart3} routePath="/analytics">
              {t("sidebar.analytics")}
            </Navigation.Button>
            <Navigation.Button icon={Blocks} routePath="/integrations">
              {t("sidebar.integrations")}
            </Navigation.Button>
            <Navigation.Button icon={LifeBuoy} routePath="/support">
              {t("sidebar.support")}
            </Navigation.Button>
            <Navigation.Button icon={FileSearch} routePath="/sensor-manuals">
              {t("sidebar.sensorManuals")}
            </Navigation.Button>
            <Navigation.Button icon={ShieldCheck} routePath="/security">
              {t("sidebar.security")}
            </Navigation.Button>
            {/* Admin-only: the every-base-station fan-out ignores installers. */}
            {role === UserRole.ADMIN && (
              <Navigation.Button icon={BellRing} routePath="/notifications">
                {t("sidebar.notifications")}
              </Navigation.Button>
            )}
          </>
        ) : null}
      </Navigation>

      <View flex={1} justifyContent="flex-end" gap="$4">
        {isOperator && (
          <Navigation>
            <Navigation.Button
              icon={LogOut}
              isLoading={isLoggingOut}
              onPress={onLogOutPress}
            >
              {t("sidebar.logout")}
            </Navigation.Button>
          </Navigation>
        )}
        {/* Baked in at build time by webpack.config.js (package.json version + short sha). */}
        <Text color="$gray10" fontSize="$3" textAlign="center">
          {t("sidebar.version", { version: process.env.APP_VERSION })}
        </Text>
      </View>
    </YStack>
  );
};
